import { getCurrentInstance, reactive, computed, toValue, ref, readonly, nextTick, h, toRef, watch, onMounted, onBeforeUnmount } from 'vue';
import { g as groupData, d as debounce } from './groupData-Dl8C-s_B.js';
import { createGlobalState, useEventBus, useElementBounding } from '@vueuse/core';
import { v4 } from 'uuid';
import { c as useId } from './useBreakpoints-Cx1EETam.js';

function useClipboard() {
  const write = async (content) => {
    return navigator.clipboard.writeText(content);
  };
  const read = async () => {
    return navigator.clipboard.readText();
  };
  return {
    write,
    read
  };
}

function useDeprecated(options = {}) {
  getCurrentInstance();
}

function useGroupData() {
  const params = reactive({
    data: [],
    getGroupKey: void 0,
    initialGroupKeys: void 0,
    keyCompareFunction: void 0
  });
  const groupedData = computed(() => groupData({
    data: params.data || [],
    getGroupKey: params.getGroupKey,
    initialGroupKeys: toValue(params.initialGroupKeys),
    keyCompareFunction: params.keyCompareFunction
  }));
  return {
    params,
    groupedData
  };
}

function getInstance() {
  const modal = ref(null);
  const readonlyModal = readonly(modal);
  const hide = () => {
    modal.value = null;
  };
  const show = async (modalComponent, modalProps) => {
    hide();
    await nextTick();
    modal.value = h(modalComponent, modalProps);
    return hide;
  };
  return {
    modal: readonlyModal,
    hide,
    show
  };
}
const useModal = createGlobalState(getInstance);

const notifier = useEventBus("notify");
function useNotifier() {
  return notifier;
}

const stackingGroups = /* @__PURE__ */ new Map();
function createStackingGroup(id, offset) {
  const groupItems = ref([]);
  const rankedGroupItemValues = computed(
    () => groupItems.value.map((item) => item.value).sort((a, b) => {
      const rankComparison = toValue(a.rank) - toValue(b.rank);
      const indexComparison = a.index - b.index;
      return rankComparison * 2 + indexComparison;
    })
  );
  const groupState = computed(() => {
    const groupState2 = [];
    rankedGroupItemValues.value.reduce((topCounter, item) => {
      const { id: id2, offset: offset2, isFixed, height, enabled } = item;
      const top = isFixed ? topCounter + offset2 : 0;
      groupState2.push({
        id: id2,
        top,
        height,
        isFixed,
        enabled
      });
      return isFixed ? top + height : topCounter;
    }, toValue(offset));
    return groupState2;
  });
  const groupThreshold = computed(
    () => groupState.value.filter((item) => item.isFixed).reduce((threshhold, item) => {
      const { top, height } = item;
      return Math.max(threshhold, top + height);
    }, 0)
  );
  const registerItem = (item) => {
    groupItems.value.push(item);
  };
  const unregisterItem = (item) => {
    const index = groupItems.value.indexOf(item);
    if (index > -1)
      groupItems.value.splice(index, 1);
  };
  const getItemState = computed(() => (item) => {
    const itemValue = toValue(item);
    return groupState.value.find((groupItem) => groupItem.id === itemValue.id);
  });
  const group = computed(() => ({
    offset: toValue(offset),
    items: groupItems.value
  }));
  return {
    stackingGroupId: id,
    stackingGroup: group,
    stackingGroupItems: groupItems,
    stackingGroupState: groupState,
    stackingGroupThreshold: groupThreshold,
    registerStackingGroupItem: registerItem,
    unregisterStackingGroupItem: unregisterItem,
    getStackingGroupItemState: getItemState
  };
}
function useStackingGroup({ id = v4(), offset = 0 } = {}) {
  id = toValue(id);
  if (!stackingGroups.has(id))
    stackingGroups.set(id, createGlobalState(() => createStackingGroup(id, offset)));
  return stackingGroups.get(id)();
}

let index = 0;
function useStackingItem(el, { rank = 0, offset = 0, groupId = v4(), groupOffset = 0, enabled = true } = {}) {
  const {
    registerStackingGroupItem,
    unregisterStackingGroupItem,
    getStackingGroupItemState,
    stackingGroupThreshold
  } = useStackingGroup({ id: groupId, offset: groupOffset });
  const itemIndex = index++;
  const itemId = useId();
  const isFixed = ref(false);
  const { y, height } = useElementBounding(el);
  const item = ref({
    id: itemId,
    index: itemIndex,
    rank,
    offset,
    el,
    isFixed,
    height
  });
  const itemState = toRef(() => getStackingGroupItemState.value(item));
  const update = () => {
    if (!toValue(enabled)) {
      isFixed.value = false;
      return;
    }
    if (!isFixed.value && y.value < stackingGroupThreshold.value + toValue(offset))
      isFixed.value = true;
    else if (isFixed.value && y.value > stackingGroupThreshold.value + toValue(offset) - height.value)
      isFixed.value = false;
  };
  watch(
    () => toValue(enabled),
    update
  );
  watch(y, debounce(update, 15));
  watch(stackingGroupThreshold, update);
  onMounted(() => {
    registerStackingGroupItem(item);
  });
  onBeforeUnmount(() => {
    unregisterStackingGroupItem(item);
  });
  return {
    groupId,
    isFixed,
    itemState
  };
}

export { useDeprecated as a, useModal as b, useNotifier as c, useGroupData as d, useStackingGroup as e, useStackingItem as f, useClipboard as u };
//# sourceMappingURL=useStackingItem-DFvE8bpP.js.map
